import {
  cloneFilter,
  combineFilters,
  getFilterRowCondition,
  getHeaderFilterCondition,
  setFilterRowCondition,
  setHeaderFilterCondition,
} from "./filter";
import { DEFAULT_OPERATION } from "../constants";

function parseWidth(width) {
  if (typeof width === "number" && Number.isFinite(width)) return width;
  if (typeof width === "string" && /^\d+(\.\d+)?(px)?$/.test(width)) return parseFloat(width);

  return undefined;
}

function columnSortOrder(column) {
  const order = column.sortOrder ?? column.defaultSortOrder;

  return order === "asc" || order === "desc" ? order : undefined;
}

export function initialColumnState(column, order) {
  return {
    order,
    visible: column.visible !== false,
    width: undefined,
    sortOrder: columnSortOrder(column),
    sortIndex: column.sortIndex,
    groupIndex: column.groupIndex ?? column.defaultGroupIndex,
    fixed: !!column.fixed,
    fixedPosition: column.fixedPosition || "left",
  };
}

function initialColumnFilters(columns, filterValue) {
  return columns.reduce((result, column) => {
    let next = result;

    if (column.filterValue != null && column.filterValue !== "")
      next = setFilterRowCondition(
        next,
        column,
        column.selectedFilterOperation || DEFAULT_OPERATION[column.dataType] || "=",
        column.filterValue
      );

    if (Array.isArray(column.filterValues) && column.filterValues.length)
      next = setHeaderFilterCondition(
        next,
        column,
        column.filterType === "exclude" ? "noneof" : "anyof",
        column.filterValues
      );

    return next;
  }, filterValue);
}

function normalizeOrders(columnsState, columns) {
  const sorted = columns.slice().sort((a, b) => {
    const difference = columnsState[a.id].order - columnsState[b.id].order;

    return difference !== 0 ? difference : a.index - b.index;
  });

  const result = { ...columnsState };

  sorted.forEach((column, index) => {
    result[column.id] = { ...result[column.id], order: index };
  });

  return result;
}

export function createInitialState({ columns, filterValue, defaultFilterValue, pageSize }) {
  const columnsState = {};

  columns.forEach(column => {
    columnsState[column.id] = initialColumnState(column, column.visibleIndex ?? column.index);
  });

  const baseFilter = cloneFilter(
    filterValue !== undefined ? filterValue : defaultFilterValue ?? null
  );

  return {
    columns: normalizeOrders(columnsState, columns),
    filterValue: initialColumnFilters(columns, baseFilter),
    filterEnabled: true,
    pageIndex: 0,
    pageSize,
  };
}

function matchSavedColumns(savedColumns, columns) {
  const dataColumns = columns.filter(column => !column.isSelection);
  const used = new Set();
  const matches = [];

  savedColumns.forEach(saved => {
    if (!saved || typeof saved !== "object") return;

    const savedName = saved.name ?? saved.dataField;
    let match;

    if (savedName)
      match = dataColumns.find(
        column =>
          !used.has(column.id) &&
          ((saved.name != null && saved.name === (column.name ?? column.dataField)) ||
            (saved.dataField != null && saved.dataField === column.dataField))
      );
    else
      match = dataColumns.find(column => !used.has(column.id) && !column.name && !column.dataField);

    if (match) {
      used.add(match.id);
      matches.push({ column: match, saved });
    }
  });

  return matches;
}

function savedColumnFilters(matches) {
  return matches.reduce((result, { column, saved }) => {
    let next = result;

    if (saved.filterValue != null && saved.filterValue !== "")
      next = setFilterRowCondition(
        next,
        column,
        saved.selectedFilterOperation || DEFAULT_OPERATION[column.dataType] || "=",
        saved.filterValue
      );

    if (Array.isArray(saved.filterValues) && saved.filterValues.length)
      next = setHeaderFilterCondition(
        next,
        column,
        saved.filterType === "exclude" ? "noneof" : "anyof",
        saved.filterValues
      );

    return next;
  }, null);
}

export function applySavedState(state, saved, columns, allowedPageSizes) {
  if (!saved || typeof saved !== "object") return state;

  const nextColumns = { ...state.columns };
  const matches = Array.isArray(saved.columns) ? matchSavedColumns(saved.columns, columns) : [];

  matches.forEach(({ column, saved: savedColumn }) => {
    const current = nextColumns[column.id];

    nextColumns[column.id] = {
      ...current,
      order:
        typeof savedColumn.visibleIndex === "number" ? savedColumn.visibleIndex : current.order,
      visible: savedColumn.visible ?? current.visible,
      width: parseWidth(savedColumn.width),
      sortOrder:
        savedColumn.sortOrder === "asc" || savedColumn.sortOrder === "desc"
          ? savedColumn.sortOrder
          : undefined,
      sortIndex: savedColumn.sortIndex,
      groupIndex: typeof savedColumn.groupIndex === "number" ? savedColumn.groupIndex : undefined,
      fixed: savedColumn.fixed ?? current.fixed,
      fixedPosition: savedColumn.fixedPosition || current.fixedPosition,
    };
  });

  const filterValue =
    "filterValue" in saved
      ? cloneFilter(saved.filterValue ?? null)
      : combineFilters(state.filterValue, savedColumnFilters(matches));

  const pageSize =
    typeof saved.pageSize === "number" &&
    saved.pageSize > 0 &&
    (!Array.isArray(allowedPageSizes) || allowedPageSizes.includes(saved.pageSize))
      ? saved.pageSize
      : state.pageSize;

  return {
    ...state,
    columns: normalizeOrders(nextColumns, columns),
    filterValue,
    filterEnabled: saved.filterPanel?.filterEnabled ?? state.filterEnabled,
    pageIndex: typeof saved.pageIndex === "number" ? saved.pageIndex : state.pageIndex,
    pageSize,
  };
}

export function toSavedState(state, columns, extra = {}) {
  return {
    columns: columns
      .filter(column => !column.isSelection)
      .map(column => {
        const columnState = state.columns[column.id];
        const rowCondition = getFilterRowCondition(state.filterValue, column);
        const headerCondition = getHeaderFilterCondition(state.filterValue, column);

        return {
          visibleIndex: columnState.order,
          dataField: column.dataField,
          name: column.name ?? column.dataField,
          dataType: column.dataType,
          width: columnState.width,
          visible: columnState.visible,
          sortOrder: columnState.sortOrder,
          sortIndex: columnState.sortOrder ? columnState.sortIndex : undefined,
          groupIndex: columnState.groupIndex,
          filterValue: rowCondition ? rowCondition.value : undefined,
          selectedFilterOperation: rowCondition ? rowCondition.operation : undefined,
          filterValues: headerCondition ? headerCondition.values : undefined,
          filterType: headerCondition?.type === "noneof" ? "exclude" : undefined,
          fixed: columnState.fixed,
          fixedPosition: columnState.fixed ? columnState.fixedPosition : undefined,
        };
      }),
    allowedPageSizes: extra.allowedPageSizes,
    filterPanel: { filterEnabled: state.filterEnabled },
    filterValue: state.filterValue,
    pageIndex: state.pageIndex,
    pageSize: state.pageSize,
    searchText: "",
    selectedRowKeys: extra.selectedRowKeys ?? [],
  };
}

function sortedBySortIndex(columnsState) {
  return Object.entries(columnsState)
    .filter(([, value]) => value.sortOrder)
    .sort((a, b) => (a[1].sortIndex ?? 0) - (b[1].sortIndex ?? 0))
    .map(([id]) => id);
}

function reindexSorts(columnsState) {
  const result = { ...columnsState };

  sortedBySortIndex(result).forEach((id, index) => {
    result[id] = { ...result[id], sortIndex: index };
  });

  return result;
}

function groupedIds(columnsState) {
  return Object.entries(columnsState)
    .filter(([, value]) => typeof value.groupIndex === "number")
    .sort((a, b) => a[1].groupIndex - b[1].groupIndex)
    .map(([id]) => id);
}

function applyGroupOrder(columnsState, ids) {
  const result = {};

  Object.entries(columnsState).forEach(([id, value]) => {
    const index = ids.indexOf(id);

    result[id] = { ...value, groupIndex: index >= 0 ? index : undefined };
  });

  return result;
}

function releaseGroupSorts(columnsState, releasedIds) {
  const othersSorted = Object.entries(columnsState).some(
    ([id, value]) =>
      !releasedIds.includes(id) && value.sortOrder && typeof value.groupIndex !== "number"
  );

  if (!othersSorted) return columnsState;

  const result = { ...columnsState };

  releasedIds.forEach(id => {
    if (result[id]) result[id] = { ...result[id], sortOrder: undefined, sortIndex: undefined };
  });

  return reindexSorts(result);
}

export function gridStateReducer(state, action) {
  switch (action.type) {
    case "reset":
      return action.state;

    case "syncColumns": {
      const { columns } = action;
      const next = {};

      columns.forEach(column => {
        next[column.id] =
          state.columns[column.id] ??
          initialColumnState(column, column.visibleIndex ?? column.index);
      });

      return { ...state, columns: normalizeOrders(next, columns) };
    }

    case "columnOption": {
      const current = state.columns[action.id];

      if (!current) return state;

      return {
        ...state,
        columns: { ...state.columns, [action.id]: { ...current, ...action.patch } },
      };
    }

    case "sort": {
      const { id, order, multiple } = action;
      let columns = { ...state.columns };

      if (!multiple)
        Object.keys(columns).forEach(key => {
          if (key !== id && columns[key].sortOrder && typeof columns[key].groupIndex !== "number")
            columns[key] = { ...columns[key], sortOrder: undefined, sortIndex: undefined };
        });

      const current = columns[id];

      if (!current) return state;

      const sortIndex =
        order && !current.sortOrder ? sortedBySortIndex(columns).length : current.sortIndex;

      columns[id] = {
        ...current,
        sortOrder: order || undefined,
        sortIndex: order ? sortIndex : undefined,
      };
      columns = reindexSorts(columns);

      return { ...state, columns, pageIndex: 0 };
    }

    case "clearSorting": {
      const columns = {};

      Object.entries(state.columns).forEach(([id, value]) => {
        columns[id] =
          typeof value.groupIndex === "number"
            ? value
            : { ...value, sortOrder: undefined, sortIndex: undefined };
      });

      return { ...state, columns, pageIndex: 0 };
    }

    case "group": {
      const { id, index } = action;
      const ids = groupedIds(state.columns).filter(key => key !== id);
      const position = index == null ? ids.length : Math.max(0, Math.min(index, ids.length));

      ids.splice(position, 0, id);

      const columns = applyGroupOrder(state.columns, ids);

      if (!columns[id].sortOrder) columns[id] = { ...columns[id], sortOrder: "asc" };

      return { ...state, columns, pageIndex: 0 };
    }

    case "ungroup": {
      const ids = groupedIds(state.columns).filter(key => key !== action.id);

      return {
        ...state,
        columns: releaseGroupSorts(applyGroupOrder(state.columns, ids), [action.id]),
        pageIndex: 0,
      };
    }

    case "ungroupAll": {
      const ids = groupedIds(state.columns);

      return {
        ...state,
        columns: releaseGroupSorts(applyGroupOrder(state.columns, []), ids),
        pageIndex: 0,
      };
    }

    case "move": {
      const { id, targetOrder, columns: configs } = action;
      const ordered = configs
        .slice()
        .sort((a, b) => state.columns[a.id].order - state.columns[b.id].order)
        .map(column => column.id)
        .filter(key => key !== id);
      const position = Math.max(0, Math.min(targetOrder, ordered.length));

      ordered.splice(position, 0, id);

      const columns = { ...state.columns };

      ordered.forEach((key, index) => {
        columns[key] = { ...columns[key], order: index };
      });

      return { ...state, columns };
    }

    case "filterValue":
      return { ...state, filterValue: action.filterValue, filterEnabled: true, pageIndex: 0 };

    case "filterEnabled":
      return { ...state, filterEnabled: action.enabled, pageIndex: 0 };

    case "pageIndex":
      return { ...state, pageIndex: action.pageIndex };

    case "pageSize":
      return { ...state, pageSize: action.pageSize, pageIndex: 0 };

    default:
      return state;
  }
}
